import { FlightServices, ReviewGuardTypes } from "@checkout/states";
import { ParentState } from "@checkout/types";
import { combineServices, type ChildMachineConfig } from "@hopper-b2b/checkout";
import { merge } from "lodash-es";
import { ActionTypes } from "./actions";
import { GuardTypes } from "./guards";
import {
  Services,
  ServiceTypes,
  validateUserPassengersService,
} from "./services";
import {
  ContactAndTravelerInformationState,
  ContactAndTravelerInformationEventTypes,
  ContactAndTravelerInformationChildState,
} from "./types";

// We are managing contact and travelers in the same state machine and combined events, actions, etc.
// We are reusing existing ContactInfoSTate and TravelerInfoState's context, actions, selectors, and events etc.
// There is new actions, context, selectors, guards specific to ContactAndTravelerMachine too.
export const getContactAndTravelerMachineState = (
  config: ChildMachineConfig
) => ({
  id: ParentState.contactAndTravelerInformation,
  initial: ContactAndTravelerInformationChildState.route,
  states: merge(
    {
      [ContactAndTravelerInformationChildState.route]: {
        always: [
          {
            target: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.travelerPicker}`,
            cond: GuardTypes.haveAnyTravelersLoaded,
          },
          {
            target: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.travelerForm}`,
          },
        ],
      },
      [ContactAndTravelerInformationState.saving]: {
        invoke: {
          src: ServiceTypes.trackAddContactEvent,
          onDone: [
            {
              target:
                config.nextStateFromReview ||
                `#${config.parentMachineId}.${ParentState.cartQuote}`,
            },
            {
              target:
                config.nextState ||
                `#${config.parentMachineId}.${ParentState.cartQuote}`,
            },
          ],
          onError: [
            {
              cond: ReviewGuardTypes.visitedReview,
              target:
                config.nextStateFromReview ||
                `#${config.parentMachineId}.${ParentState.cartQuote}`,
            },
            {
              target:
                config.nextState ||
                `#${config.parentMachineId}.${ParentState.cartQuote}`,
            },
          ],
        },
      },
      [ContactAndTravelerInformationState.travelerPicker]: {
        initial: ContactAndTravelerInformationChildState.idle,
        states: {
          [ContactAndTravelerInformationChildState.idle]: {},
          // Routing state: decides whether to proceed with validation or wait for infant modal
          // This prevents validation from running while the infant seat selection modal is open
          [ContactAndTravelerInformationChildState.validate]: {
            always: [
              {
                cond: GuardTypes.shouldValidateForInfant,
                target: ContactAndTravelerInformationChildState.validating,
              },
              {
                target: ContactAndTravelerInformationChildState.idle,
              },
            ],
          },
          [ContactAndTravelerInformationChildState.validating]: {
            invoke: {
              src: (ctx, e) =>
                combineServices([
                  validateUserPassengersService(ctx, e),
                  FlightServices.fetchTripPricingService(ctx),
                ]),
              onDone: [
                {
                  actions: [
                    ActionTypes.onFetchTripPricing,
                    ActionTypes.setValidated,
                  ],
                  target: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.travelerPicker}.${ContactAndTravelerInformationChildState.idle}`,
                },
              ],
              onError: {
                target: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.error}`,
                actions: [
                  ActionTypes.dismissNumPassengerAlert,
                  ActionTypes.onValidateAndTripPricingError,
                ],
              },
            },
          },
        },
      },
      [ContactAndTravelerInformationState.travelerForm]: {
        initial: ContactAndTravelerInformationChildState.route,
        states: {
          [ContactAndTravelerInformationChildState.route]: {
            always: [
              {
                cond: GuardTypes.hasCurrentTraveler,
                target: ContactAndTravelerInformationChildState.edit,
              },
              {
                target: ContactAndTravelerInformationChildState.new,
              },
            ],
          },
          [ContactAndTravelerInformationChildState.edit]: {
            invoke: {
              src: ServiceTypes.trackEditTravelers,
              onDone: { target: ContactAndTravelerInformationChildState.idle },
              onError: { target: ContactAndTravelerInformationChildState.idle },
            },
          },
          [ContactAndTravelerInformationChildState.new]: {
            invoke: {
              src: ServiceTypes.trackNewTraveler,
              onDone: { target: ContactAndTravelerInformationChildState.idle },
              onError: { target: ContactAndTravelerInformationChildState.idle },
            },
          },
          [ContactAndTravelerInformationChildState.idle]: {},
          [ContactAndTravelerInformationChildState.update]: {
            invoke: {
              src: ServiceTypes.updateTravelerService,
              onDone: {
                target: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.travelerPicker}.${ContactAndTravelerInformationChildState.validate}`,
                actions: [
                  ActionTypes.updateTraveler,
                  ActionTypes.clearTravelerInformationError,
                  ActionTypes.selectTravelerAfterUpdate,
                  (ctx) => Services.trackSaveTraveler(ctx),
                ],
              },
              onError: {
                target: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.error}`,
                actions: ActionTypes.setTravelersError,
              },
            },
          },
        },
      },
      [ContactAndTravelerInformationState.validate]: {
        invoke: {
          src: (ctx, e) =>
            combineServices([
              validateUserPassengersService(ctx, e),
              FlightServices.fetchTripPricingService(ctx),
            ]),
          onDone: [
            {
              cond: ReviewGuardTypes.visitedReview,
              target: config.nextStateFromReview,
              actions: [
                ActionTypes.onFetchTripPricing,
                ActionTypes.setValidated,
              ],
            },
            {
              target:
                config.nextState || ParentState.contactAndTravelerInformation,
              actions: [
                ActionTypes.onFetchTripPricing,
                ActionTypes.setValidated,
              ],
            },
          ],
          onError: {
            target: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.error}`,
            actions: [
              ActionTypes.dismissNumPassengerAlert,
              ActionTypes.onValidateAndTripPricingError,
            ],
          },
        },
      },
      [ContactAndTravelerInformationState.deleteModal]: {
        initial: ContactAndTravelerInformationChildState.route,
        states: {
          [ContactAndTravelerInformationChildState.route]: {
            always: [
              {
                cond: GuardTypes.hasCurrentTraveler,
                target: ContactAndTravelerInformationChildState.edit,
              },
              {
                target: ContactAndTravelerInformationChildState.new,
              },
            ],
          },
          [ContactAndTravelerInformationChildState.edit]: {},
          [ContactAndTravelerInformationChildState.new]: {
            invoke: {
              src: ServiceTypes.trackNewTraveler,
              onDone: { target: ContactAndTravelerInformationChildState.idle },
              onError: { target: ContactAndTravelerInformationChildState.idle },
            },
          },
          [ContactAndTravelerInformationChildState.idle]: {},
          [ContactAndTravelerInformationChildState.delete]: {
            invoke: {
              src: ServiceTypes.deleteTravelerService,
              onDone: {
                actions: ActionTypes.handleDeleteTraveler,
                target: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.travelerPicker}.${ContactAndTravelerInformationChildState.idle}`,
              },
              onError: {
                actions: ActionTypes.setTravelersError,
                target: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.error}`,
              },
            },
          },
        },
      },
      [ContactAndTravelerInformationState.error]: {},
    },
    config.stateOverride
  ),
  on: {
    [ContactAndTravelerInformationEventTypes.CHANGE]: {
      actions: ActionTypes.setContactInfo,
    },
    [ContactAndTravelerInformationEventTypes.VALIDATE_TRAVELER]: {
      target: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.travelerPicker}.${ContactAndTravelerInformationChildState.validate}`,
      cond: GuardTypes.willHaveSelectedTravelers,
    },
    [ContactAndTravelerInformationEventTypes.CHANGE_PHONE_NUMBER]: {
      actions: ActionTypes.setContactInfoPhoneNumber,
    },
    [ContactAndTravelerInformationEventTypes.CHANGE_EMAIL]: {
      actions: ActionTypes.setContactInfoEmail,
    },
    [ContactAndTravelerInformationEventTypes.NEXT]: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.saving}`,
    [ContactAndTravelerInformationEventTypes.SELECT_TRAVELER]: [
      {
        actions: [ActionTypes.selectTraveler],
        target: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.travelerPicker}.${ContactAndTravelerInformationChildState.validate}`,
        cond: GuardTypes.willHaveSelectedTravelers,
      },
      {
        actions: ActionTypes.selectTraveler,
      },
    ],
    [ContactAndTravelerInformationEventTypes.UPDATE_TRAVELER]: {
      target: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.travelerForm}.${ContactAndTravelerInformationChildState.update}`,
    },
    [ContactAndTravelerInformationEventTypes.DELETE_TRAVELER]: {
      target: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.deleteModal}.${ContactAndTravelerInformationChildState.delete}`,
    },
    [ContactAndTravelerInformationEventTypes.SET_CURRENT_TRAVELER]: {
      actions: ActionTypes.setCurrentTraveler,
    },
    [ContactAndTravelerInformationEventTypes.CLEAR_TRAVELER_INFORMATION_ERROR]:
      {
        actions: ActionTypes.clearTravelerInformationError,
        target: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.travelerPicker}`,
      },
    [ContactAndTravelerInformationEventTypes.SET_CURRENT_INFANT_ID]: {
      actions: ActionTypes.setCurrentInfantId,
    },
    [ContactAndTravelerInformationEventTypes.ON_INFANT_MODAL_CONTINUE]: {
      actions: ActionTypes.onInfantModalContinue,
      target: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.travelerPicker}.${ContactAndTravelerInformationChildState.validate}`,
    },
    [ContactAndTravelerInformationEventTypes.CLEAR_CURRENT_INFANT_ID]: {
      actions: ActionTypes.clearCurrentInfantId,
    },
    [ContactAndTravelerInformationEventTypes.SET_TRAVELER_ERROR]: {
      actions: ActionTypes.setTravelersError,
      target: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.error}`,
    },
    [ContactAndTravelerInformationEventTypes.OPEN_TRAVELER_PICKER]: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.travelerPicker}`,

    [ContactAndTravelerInformationEventTypes.OPEN_TRAVELER_FORM]: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.travelerForm}`,

    [ContactAndTravelerInformationEventTypes.OPEN_TRAVELER_DELETE_MODAL]: `#${ParentState.contactAndTravelerInformation}.${ContactAndTravelerInformationState.deleteModal}`,
  },
});
